/**
 * @file 广场
 * @author Yongning 2024-09-10
 */

import tdk from '@/constants/tdk';
import ReactDOM from 'react-dom';
import styles from './index.module.scss';
import { CommonHead } from '@/components';
import { IUserLoginInfo } from '@/api/user';
import { namespace, IState } from '@/context';
import { GetServerSidePropsContext } from 'next';
import ContentCard from '@/components/ContentCard';
import { getNavsList, ITopLevelNav } from '@/api/nav';
import TabSearchComponent from "@/components/TabSearch";
import useTabSearchHook from '../home/hooks/useTabSearchHook';
import getMyCircle, { ICircle } from '@/api/ channel/myCircle';
import getThreadList, { IThreadLisItem } from "@/api/thread/list";
import Carousel, { AttachType, CarouselItem } from '@/components/Carousel';
import { 
  IHotThread,
  getHotThreads,
  getChannelDetail,
  TopLevelChannels,
  BannerConfigType,
  getSubChannelTree,
  ISubChannelTreeRes,
} from '@/api/home';
import useBodyContainer from '@/hooks/useBodyContainer';



interface Error {
  message: string;
  code: string | number;
};

export interface SquareProps {
  ua: string;
  error?: Error[];
  saltkey: string;
  otList: IHotThread[];
  account?: IUserLoginInfo;
  sources: ISubChannelTreeRes;
  defaultChannelActiveSlug: string;
  defaultChannelActiveSort: string;
  channelList: ISubChannelTreeRes[];
  firstPageArticalList: IState[namespace.square]['articalList'][string]['data'];
}

const Square = (props: SquareProps) => {
  const container = useBodyContainer();

  const { channelList, firstPageArticalList, sources } = props;
  const {tabs, moreTabs, tabSortFilterMap} = useTabSearchHook(channelList);

  // 轮播图数据
  const bannerList: CarouselItem[] = (sources?.channel_config?.banner_config || []).map(item => {
    const { link_url, pc_banner_url, video_play_info } = item;
    const hasVideo = video_play_info?.video_uid;
  
    return {
      alt: '',
      link: {
        src: link_url,
      },
      type: hasVideo ? AttachType.Video : AttachType.Image,
      src: hasVideo ? video_play_info.definitions[1].src : pc_banner_url,
      ...(hasVideo && { fallbackSrc: pc_banner_url })
    };
  });
  return (
    <div
      className={styles.squareMainContent}
      style={{marginTop: !bannerList.length ? '0' : '16px'}}
    >
      <CommonHead {...tdk[namespace.square]} />
      {container &&
        ReactDOM.createPortal(
        '广场 - 大疆社区',
        container
      )}
      {
        !!bannerList.length && (
          <div className={styles.bannerContent}>
            <Carousel items={bannerList} width={640} height={120} />
          </div>
        )
      }
      <TabSearchComponent<IThreadLisItem>
        channel={TopLevelChannels.home_page_plaza_channel}
        id="tid"
        tabs={tabs}
        pageSize={10}
        moreTabs={moreTabs}
        Component={ContentCard}
        tabSortFilterMap={tabSortFilterMap}
        defaultContent={firstPageArticalList}
        fetchApi={({ tabKey, sortKey, offset = 10, pageSize}) => getThreadList({
          offset,
          limit: pageSize,
          sort_type: sortKey,
          channel_slug: tabKey,
        })}
      />
  </div>
  )
};

const handlePromiseResult = (result:  PromiseSettledResult<any>, setValue: ((value: any) => void), errorList: Error[]) => {
  if (result?.status === 'fulfilled') {
    setValue(result.value || []);
  } else {
    errorList.push(result.reason);
  }
};

export async function getServerSideProps(ctx: GetServerSidePropsContext) {
  const ua = ctx.req.headers['user-agent'] || '';
  const headers = ctx.req ? ctx.req.headers : {};

  let error: Error[] = [];
  let circleList: ICircle[] = [];
  let hotList: IHotThread[] = [];
  let navList: ITopLevelNav[] = [];
  let bannerList: BannerConfigType[] = [];
  let channelList: ISubChannelTreeRes[] = [];
  let firstPageArticalList: IThreadLisItem[] = [];
  let defaultValues = {
    defaultChannelActiveSort: '',
    defaultChannelActiveSlug: '',
  };
  let sources: ISubChannelTreeRes | [] = {} as unknown as ISubChannelTreeRes;


  try {
    channelList =
      (
        await getSubChannelTree(
          {
            slug: TopLevelChannels.home_page_plaza_channel,
            level_num: 2,
          },
          true,
          headers,
        )
      )?.sub_channel_info_dtos || [];


    defaultValues = {
      defaultChannelActiveSort: channelList[0].order_rule[0],
      defaultChannelActiveSlug: channelList[0].slug,
    };

    const [artical, circle, navs, cmsSources, hots ] = await Promise.allSettled([
      getThreadList(
        {
          channel_slug: defaultValues?.defaultChannelActiveSlug,
          sort_type: defaultValues.defaultChannelActiveSort,
        },
        true,
        headers,
      ),
      getMyCircle(true, headers),
      getNavsList(true),
      getChannelDetail(
        {
          slug: TopLevelChannels.home_page_plaza_channel,
        },
        true,
        headers,
      ),
      getHotThreads(true),
    ]);

    // 处理请求结果
    handlePromiseResult(navs, (value: typeof navList) => (navList = value), error);
    handlePromiseResult(hots, (value: typeof hotList) => (hotList = value), error);
    handlePromiseResult(cmsSources, (value: typeof sources) => (sources = value), error);
    handlePromiseResult(circle, (value: typeof circleList) => (circleList = value), error);
    handlePromiseResult(artical, (value: typeof firstPageArticalList) => (firstPageArticalList = value), error);

    // 兜底：首屏数据为空时，尝试请求 offset=10（后端 offset=0 可能返回空但 offset=10 有数据）
    if (!firstPageArticalList?.length) {
      try {
        firstPageArticalList = await getThreadList(
          {
            channel_slug: defaultValues?.defaultChannelActiveSlug,
            sort_type: defaultValues.defaultChannelActiveSort,
            offset: 10,
          },
          true,
          headers,
        );
      } catch (e) {}
    }
  } catch (e) {
  }

  const matchCookies = ctx.req.headers.cookie?.match(/H5VF_2132_saltkey=(\w+)/);

  return {
    props: {
      ua,
      error,
      navList,
      hotList,
      sources,
      circleList,
      bannerList,
      channelList,
      firstPageArticalList,
      saltkey: matchCookies && matchCookies.length >= 2 ? matchCookies[1] : '',
      defaultChannelActiveSlug: defaultValues?.defaultChannelActiveSlug || null,
      defaultChannelActiveSort: defaultValues?.defaultChannelActiveSort || null,
    },
  };
};

export default Square;
